import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PageHeader from '../../components/PageHeader';
import DataList from '../../components/DataList';
import { getUploadsTableConfig, PAGE_CONFIG } from './config';
import EmptyTableIndicator from '../../components/EmptyTableIndicator';
import { faFileContract } from '@fortawesome/free-solid-svg-icons';
import ActionToolbar from '../../components/ActionToolbar';
import AddButton from '../../components/AddButton';
import UploadModal from './UploadModal';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../context/ThemeProvider';
import { useTranslation } from 'react-i18next';

// Import custom hooks
import { useModals } from '../../hooks/useModals';
import {
    useCreateUpload,
    useUpdateUpload,
    useDeleteUpload,
    useUploadForm,
    useUploadActions
} from './hooks/index';
import api from '../../api';

const Upload = () => {
    const { t } = useTranslation();
    const tr = useCallback((key) => t(`uploadAdmin:${key}`), [t]);

    const { theme } = useTheme();
    const queryClient = useQueryClient();

    // Add state for selected material type
    const [selectedMaterialType, setSelectedMaterialType] = useState(null);
    //   Start of new implementation

    //  Pagination state management
    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        search: ''
    });

    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        setParams((prev) => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (limit) => {
        if (limit < 1) return;
        setParams((prev) => ({ ...prev, limit }));
    };

    // get material types
    const { data: materialTypes } = useQuery({
        queryKey: ['materialTypes'],
        queryFn: () => api.materialTypes.getMaterialTypes(),
        select: (data) => data?.data?.data
    });

    // Combine materialTypeId with other params
    const queryParams = useMemo(
        () => ({
            ...params,
            materialTypeId: selectedMaterialType?.id
        }),
        [params, selectedMaterialType]
    );

    // Use useQuery with proper pagination handling
    const { data: dataResponse, isLoading } = useQuery({
        queryKey: ['data', queryParams],
        queryFn: () => api.upload.getUploads(queryParams),
        enabled: !!selectedMaterialType?.id, // Only run when materialTypeId is available
        select: (data) => data?.data
    });

    // Extract data and pagination from response
    const data = dataResponse?.data;
    const pagination = {
        page: params.page,
        limit: params.limit,
        totalCount: dataResponse?.totalCount || 0
    };
    const totalPages = Math.ceil(pagination.totalCount / pagination.limit);

    //   End of new implementation

    // Modal management hook
    const { isOpen, isDeleteOpen, selectedItem, handleOpenModal, handleCloseModal, handleOpenDeleteModal, handleCloseDeleteModal } = useModals();

    // CRUD hooks
    const { createUpload, createStatus } = useCreateUpload(queryClient, theme);
    const { updateUpload, updateStatus } = useUpdateUpload(queryClient, theme);
    const { deleteUpload, isDeleting } = useDeleteUpload(queryClient, theme, handleCloseDeleteModal);


    // Set first material type as active by default
    useEffect(() => {
        if (materialTypes && materialTypes.length > 0 && !selectedMaterialType) {
            setSelectedMaterialType(materialTypes[0]);
        }
    }, [materialTypes, selectedMaterialType]);

    // Handle material type selection
    const handleMaterialTypeClick = useCallback(
        (materialType) => {
            setSelectedMaterialType(selectedMaterialType?.id === materialType.id ? null : materialType);
        },
        [selectedMaterialType]
    );

    // Form management hook
    const { form, files, errors, isEdit, selectedItem: formSelectedItem, handleInputChange, handleFileChange, resetForm, setEditData, handleSubmit } = useUploadForm(createUpload, updateUpload, handleCloseModal, materialTypes);

    // Actions hook
    const { downloadingId, handleEdit, handleDownload, truncateDescription: truncate } = useUploadActions(theme, handleOpenDeleteModal, setEditData);

    // Enhanced handlers that combine modal and form logic
    const handleOpenCreateModal = useCallback(() => {
        handleOpenModal();
        resetForm();
    }, [handleOpenModal, resetForm]);

    const handleCloseModalWithReset = useCallback(() => {
        handleCloseModal();
        resetForm();
    }, [handleCloseModal, resetForm]);

    const handleEditWithModal = useCallback(
        (item) => {
            handleOpenModal();
            handleEdit(item);
        },
        [handleOpenModal, handleEdit]
    );

    const handleDeleteWithModal = useCallback(
        (item) => {
            handleOpenDeleteModal(item);
        },
        [handleOpenDeleteModal]
    );

    
   

    // Table configuration
    const columns = useMemo(() => getUploadsTableConfig(truncate, handleEditWithModal, handleDeleteWithModal, handleDownload, downloadingId), [truncate, handleEditWithModal, handleDeleteWithModal, handleDownload, downloadingId]);

    return (
        <div className="flex">
            <div className="flex-1">
                <PageHeader items={PAGE_CONFIG.BREADCRUMB} pageTitle={PAGE_CONFIG.PAGE_TITLE} />
                <ActionToolbar searchQuery={params.search} onSearchChange={(e) => setParams({ ...params, search: e.target.value })} actionButton={<AddButton isLoading={false} onClick={handleOpenCreateModal} label={tr('create')} />} disabled={false} />

                {/* Material Types */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {materialTypes?.map((materialType) => (
                        <div
                            key={materialType.id}
                            className={`p-2 rounded-md cursor-pointer transition-colors duration-200 ${
                                selectedMaterialType?.id === materialType.id ? 'bg-[#50A1CA] text-white dark:bg-[#50A1CA]' : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                            onClick={() => handleMaterialTypeClick(materialType)}
                        >
                            {materialType.name}
                        </div>
                    ))}
                </div>

                {data?.length > 0 ? (
                    <DataList data={data} columns={columns} pagination={pagination} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />
                ) : (
                    <EmptyTableIndicator colSpan={5} isLoading={isLoading} icon={faFileContract} description={PAGE_CONFIG.SEARCH_DESCRIPTION} placeholder={PAGE_CONFIG.SEARCH_PLACEHOLDER} loadingMessage="Loading..." />
                )}
                <UploadModal
                    handleFileChange={handleFileChange}
                    handleChange={handleInputChange}
                    handleSubmit={handleSubmit}
                    materialTypes={materialTypes}
                    form={form}
                    isOpen={isOpen}
                    onClose={handleCloseModalWithReset}
                    status={createStatus}
                    isEdit={isEdit}
                    selectedItem={formSelectedItem}
                    setIsEdit={setEditData}
                    onSubmit={handleSubmit}
                    isLoading={createStatus === 'pending' || updateStatus === 'pending'}
                    errors={errors}
                />

                <ConfirmationDialog
                    title={tr('deleteHeader')}
                    onConfirm={() => deleteUpload(selectedItem?.id)}
                    type="DELETE"
                    message={tr('deleteMessage', { title: selectedItem?.title?.length > 50 ? selectedItem?.title?.substring(0, 50) + '...' : selectedItem?.title })}
                    isOpen={isDeleteOpen}
                    onClose={handleCloseDeleteModal}
                    isLoading={isDeleting}
                />
            </div>
        </div>
    );
};

export default Upload;
